import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, useAlert } from "@digital-services/ui";

import { ApplicationAssociations } from "@users/applicationuser/components/ApplicationAssociations";
import { useInviteApplicationUser } from "@users/applicationuser/services/ApplicationUserService";
import { useInviteOrganizationUser } from "@users/organizationuser/services/OrganizationUserService";

import { useOrganizationContext } from "@hooks/useOrganizationContext";

import { useInviteFormContext } from "../context";
import { AssociatedApplicationRoles } from "../models/AssociatedApplicationRoles";
import { DisplayNameExtended } from "./section-top/DisplayNameExtended";
import { OrganizationRoleDropDown } from "./section-top/OrganizationRoleDropDown";
import { UserEmailExtended } from "./section-top/UserEmailExtended";

interface Props {
  onCancel: () => void;
  onSuccess: () => void;
}

export function InviteUserForm(props: Readonly<Props>) {
  const { onCancel, onSuccess } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.users"
  });
  const { t: tUser } = useTranslation(undefined, {
    keyPrefix: "user"
  });
  const { t: tApplication } = useTranslation(undefined, {
    keyPrefix: "application.users"
  });
  const { t: tCommon } = useTranslation();

  const { organization } = useOrganizationContext();

  const { invite: inviteApplicationUser } = useInviteApplicationUser();
  const { notifyError, notifySuccess } = useAlert();
  const { invite } = useInviteOrganizationUser();

  //
  const [localDisplayName, setLocalDisplayName] = useState<string>("");
  const [isValidDisplayName, setIsValidDisplayName] = useState<boolean>(false);

  const [email, setEmail] = useState<string>("");
  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);

  const {
    contextDisplayName,
    setContextDisplayName,
    contextDisableSubmit,
    contextReInviteMode
  } = useInviteFormContext();
  //

  const [organizationRole, setOrganizationRole] = useState<string>("");
  const [isValidOrganizationRole, setIsValidOrganizationRole] =
    useState<boolean>(false);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [selectedApplications, setSelectedApplications] = useState<
    AssociatedApplicationRoles[]
  >([]);
  const [isValidRoles, setIsValidRoles] = useState<boolean>(true);

  useEffect(() => {
    // When we use context value it doesn't go through regular <Input> field validation, so we need this
    if (contextDisplayName && contextDisplayName !== localDisplayName) {
      setIsValidDisplayName(true);
    }
  }, [contextDisplayName, localDisplayName]);

  const disableSave =
    contextDisableSubmit ||
    !isValidEmail ||
    !isValidDisplayName ||
    !isValidOrganizationRole ||
    !isValidRoles;

  const disableDisplayField =
    contextDisplayName.length > 0 && contextDisplayName !== localDisplayName;

  const finalDisplayNameValue = contextDisplayName || localDisplayName;

  const saveChanges = () => {
    setIsBusy(true);
    inviteUser()
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        notifyError({
          header: t("invite.error.message", {
            user: finalDisplayNameValue,
            organization: organization.name
          }),
          "data-testid": "invite-user-save-error"
        });
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const inviteUser = async (): Promise<void> => {
    if (disableSave) {
      return;
    }

    const apiResponse = await invite({
      displayName: finalDisplayNameValue,
      emailAddress: email,
      organizationRole: organizationRole
    });

    if (apiResponse.isSuccess) {
      notifySuccess({
        header: t("invite.success.message", {
          user: finalDisplayNameValue,
          organization: organization.name
        }),
        "data-testid": "invite-user-save-success"
      });

      await associateSelectedApps(apiResponse.data!.userId);
    } else {
      notifyError({
        header: t("invite.error.message", {
          user: finalDisplayNameValue,
          organization: organization.name,
          reason: apiResponse.errorMessage
        }),
        "data-testid": "invite-user-save-error"
      });
    }
  };

  const associateSelectedApps = async (userId: string) => {
    for (const item of selectedApplications) {
      const { isSuccess } = await inviteApplicationUser(
        item.applicationId,
        userId,
        item.userRoles
      );

      const options = {
        user: finalDisplayNameValue,
        application: item.applicationName
      };

      if (isSuccess) {
        notifySuccess({
          header: tApplication("add.success.message", options),
          "data-testid": "associate-application-user-save-success"
        });
      } else {
        notifyError({
          header: tApplication("add.error.message", options),
          "data-testid": "associate-application-user-save-error"
        });
      }
    }
  };

  function handleDisplayNameChange(newValue: string, isValid: boolean): void {
    setLocalDisplayName(newValue);
    setIsValidDisplayName(isValid);
    setContextDisplayName(newValue);
  }

  function handleEmailChange(newValue: string, isValid: boolean): void {
    setEmail(newValue);
    setIsValidEmail(isValid);
  }

  function handleRoleChange(newValue: string, isValid: boolean) {
    setOrganizationRole(newValue);
    setIsValidOrganizationRole(isValid);
  }

  function handleAppRoleChange(
    applicationId: string,
    applicationName: string,
    applicationRoles: string[],
    isValid: boolean,
    isChecked: boolean
  ) {
    const selectedAppRole = new AssociatedApplicationRoles(
      applicationId,
      applicationName,
      applicationRoles
    );

    setSelectedApplications(current => {
      const applications = [...current];

      const index = applications.findIndex(
        item => item.applicationId === applicationId
      );
      if (index > -1) applications.splice(index, 1);

      if (isChecked) applications.push(selectedAppRole);

      return applications;
    });

    let localIsValid = isValid;
    if (selectedAppRole?.userRoles.length === 0) {
      // Use case of applications like "AMP"
      localIsValid = true;
    }

    if (selectedApplications?.length === 0) {
      // Use case when no applications selected yet, or applications were unselected
      localIsValid = true;
    }

    setIsValidRoles(localIsValid);
  }

  return (
    <div className="flex flex-col gap-10" data-testid="invite-user-content">
      <UserEmailExtended
        id="emailAddressAdvanced"
        label={tUser("email.label")}
        initialEmailValue={email}
        onEmailChange={handleEmailChange}
      />

      <DisplayNameExtended
        id="displayNameAdvanced"
        label={tUser("display-name.label")}
        initialNameValue={finalDisplayNameValue}
        disabled={disableDisplayField || isBusy}
        onDisplayNameChange={handleDisplayNameChange}
      />

      <OrganizationRoleDropDown
        label={tUser("role.label")}
        selectedRole={organizationRole}
        disabled={isBusy}
        onRoleChange={handleRoleChange}
      />

      <ApplicationAssociations
        onAppRoleChange={handleAppRoleChange}
        isDisabled={isBusy}
      />

      <div className="flex flex-row-reverse gap-4">
        <Button
          data-testid="save"
          variant="primary"
          isLoading={isBusy}
          onClick={saveChanges}
          disabled={disableSave}
        >
          {contextReInviteMode
            ? tCommon("button.re-invite.label")
            : tCommon("button.invite.label")}
        </Button>
        <Button
          data-testid="cancel"
          onClick={() => onCancel()}
          disabled={isBusy}
        >
          {tCommon("button.cancel.label")}
        </Button>
      </div>
    </div>
  );
}
